<script lang="ts">
  import { joinDatetimeLocal, splitDatetimeLocal } from "$lib/utils/datetime";
  import { cn } from "$lib/utils/cn";
  import * as m from "$lib/paraglide/messages";
  import Input from "./Input.svelte";
  import Label from "./Label.svelte";

  type Props = {
    id: string;
    label?: string;
    value?: string;
    allDay?: boolean;
    required?: boolean;
    class?: string;
    /** Show quick time chips under the time field. */
    presets?: boolean;
  };

  let {
    id,
    label,
    value = $bindable(""),
    allDay = false,
    required = false,
    class: className,
    presets = true,
  }: Props = $props();

  let date = $state("");
  let time = $state("");
  let lastExternal = $state<string | null>(null);
  let syncing = $state(false);

  const TIME_PRESETS = ["08:00", "09:00", "10:00", "12:00", "14:00", "16:00", "18:00"] as const;

  $effect(() => {
    if (value === lastExternal) return;
    syncing = true;
    lastExternal = value;
    const parts = splitDatetimeLocal(value);
    date = parts.date;
    time = parts.time;
    syncing = false;
  });

  $effect(() => {
    void date;
    void time;
    void allDay;
    if (syncing) return;

    if (!allDay && date && !time) {
      time = "09:00";
      return;
    }

    const next = joinDatetimeLocal(date, time, allDay);
    if (next === lastExternal) return;
    lastExternal = next;
    value = next;
  });

  function pickPreset(slot: string) {
    time = slot;
    if (!date) {
      const now = new Date();
      const pad = (n: number) => String(n).padStart(2, "0");
      date = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
    }
  }
</script>

<div class={cn("space-y-1.5", className)}>
  {#if label}
    <Label for={id} {required}>{label}</Label>
  {/if}

  <div class={cn("grid gap-2", allDay ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-[1.25fr_1fr]")}>
    <div>
      <span class="mb-1 block text-[11px] font-medium text-(--surface-fg-subtle)">{m.form_date()}</span>
      <Input id={id} type="date" {required} bind:value={date} />
    </div>
    {#if !allDay}
      <div>
        <span class="mb-1 block text-[11px] font-medium text-(--surface-fg-subtle)">{m.form_time()}</span>
        <Input id={`${id}-time`} type="time" step="900" bind:value={time} />
      </div>
    {/if}
  </div>

  {#if !allDay && presets}
    <div class="flex flex-wrap gap-1.5 pt-0.5">
      {#each TIME_PRESETS as slot (slot)}
        <button
          type="button"
          class={cn(
            "rounded-md border px-2 py-0.5 text-[11px] font-medium transition-colors",
            time === slot
              ? "border-(--surface-accent) bg-(--surface-accent) text-(--surface-accent-fg)"
              : "border-(--surface-border) text-(--surface-fg-muted) hover:border-(--surface-border-strong) hover:text-(--surface-fg)",
          )}
          onclick={() => pickPreset(slot)}
        >
          {slot}
        </button>
      {/each}
    </div>
  {/if}
</div>
